import { type ReactNode } from "react";
import { Link } from "wouter";
import { ShieldAlert } from "lucide-react";
import { useRole } from "@/contexts/AuthContext";

/**
 * Garde de rôle : réserve une route aux ARBITRE / ADMIN.
 *
 * Le rôle PARTIE (avocat) ne doit JAMAIS accéder aux outils juridictionnels
 * (rédaction de sentence / SentenceCompanion, brouillons de décision) —
 * séparation stricte des prérogatives (Art. 20 AUA ; cf. masterplan Cockpit
 * Avocat §14). Côté serveur, ces endpoints sont déjà interdits à la PARTIE
 * (RBAC + RLS) ; ce garde est la barrière UX correspondante.
 *
 * À utiliser à l'intérieur d'un <ProtectedRoute> (l'authentification est déjà
 * résolue). On bloque uniquement le rôle PARTIE : un ARBITRE / ADMIN — ou un
 * rôle non résolu — passe, pour ne jamais verrouiller un arbitre légitime par
 * erreur (defense en profondeur côté serveur de toute façon).
 */
export function ArbiterOnlyRoute({ children }: { children: ReactNode }) {
  const role = useRole();

  if (role === "PARTIE") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "oklch(0.115 0.038 243)", color: "oklch(1 0 0)" }}
      >
        <div className="max-w-md text-center space-y-4">
          <ShieldAlert
            size={40}
            style={{ color: "oklch(0.695 0.105 68)", margin: "0 auto" }}
          />
          <h1 className="text-xl font-semibold">Espace réservé à l'arbitre</h1>
          <p className="text-sm" style={{ opacity: 0.8 }}>
            La rédaction des décisions et sentences relève de la juridiction
            arbitrale. En tant qu'avocat (partie), vous n'avez pas accès à cet
            outil. Vous pourrez consulter les décisions une fois notifiées.
          </p>
          <Link
            href="/dashboard"
            className="inline-block text-sm underline"
            style={{ color: "oklch(0.695 0.105 68)" }}
          >
            ← Retour au tableau de bord
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
